import { useState } from 'react';
import { Share2, Loader2 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Checkbox } from './ui/checkbox';
import { Button } from './ui/button';
import { toast } from 'sonner';

export const FIELD_ROLE_OPTIONS = [
  { value: 'operative', label: 'Operative', short: 'Operative' },
  { value: 'sub_contractor', label: 'Subcontractor', short: 'Subcontractor' },
  { value: 'surveyor', label: 'Surveyor', short: 'Surveyor' },
];

const toShareFile = (f, folder) => ({ id: f.id, name: f.name, size: f.size ?? null, modified_at: f.modified_at ?? null, folder: folder || '' });

export const shareLabel = (roles) => {
  if (!roles || roles.length === 0) return null;
  return FIELD_ROLE_OPTIONS.filter(o => roles.includes(o.value)).map(o => o.short).join(', ');
};

/** Badge shown on every file in the admin browser: "Not shared" or who it is shared with. */
export const ShareBadge = ({ roles, fileId }) => {
  const label = shareLabel(roles);
  return label ? (
    <span className="inline-flex items-center rounded-full bg-emerald-500/15 text-emerald-300 px-1.5 py-px text-[10px] font-medium" data-testid={`file-share-badge-${fileId}`}>
      Visible to: {label}
    </span>
  ) : (
    <span className="inline-flex items-center rounded-full bg-white/5 text-gray-500 px-1.5 py-px text-[10px] font-medium" data-testid={`file-share-badge-${fileId}`}>
      Not visible to field
    </span>
  );
};

/** Small popover with one checkbox per field role. `files` = one file or every file in a folder. */
export const FileShareControl = ({ api, projectId, files, folder, roles, onSaved, compact = false, label, testId }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(roles || []);
  const [saving, setSaving] = useState(false);
  const bulk = files.length > 1;

  const toggle = (value) => setDraft(d => (d.includes(value) ? d.filter(v => v !== value) : [...d, value]));

  const save = async () => {
    setSaving(true);
    try {
      if (bulk) {
        await api.post(`/projects/${projectId}/file-shares/bulk`, { visible_to_roles: draft, files: files.map(f => toShareFile(f, folder)) });
      } else {
        await api.put(`/projects/${projectId}/file-shares/${files[0].id}`, { visible_to_roles: draft, file: toShareFile(files[0], folder) });
      }
      toast.success(draft.length ? `Visible to ${shareLabel(draft)}` : 'Hidden from the field');
      onSaved?.(files.map(f => f.id), draft);
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update sharing');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setDraft(roles || []); }}>
      <PopoverTrigger asChild>
        {compact ? (
          <button type="button" className="p-1.5 rounded-md bg-black/60 text-gray-200 hover:text-white" title="Visible to" data-testid={testId}>
            <Share2 className="w-3.5 h-3.5" />
          </button>
        ) : (
          <Button size="sm" variant="ghost" className="text-gray-400 hover:text-white hover:bg-white/10" title="Visible to" data-testid={testId}>
            <Share2 className="w-4 h-4" />
            {label && <span className="ml-1.5 text-xs">{label}</span>}
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 bg-[#1E2530] border-white/10 text-white p-3" data-testid="file-share-popover">
        <p className="text-sm font-medium">Visible to</p>
        <p className="text-[11px] text-gray-400 mt-0.5 mb-2.5">
          {label ? `Applies to the ${files.length} file${files.length === 1 ? '' : 's'} currently in this folder. Files added later start hidden.` : 'Ticked roles see this file in the portal — only on jobs they are assigned or scheduled to.'}
        </p>
        <div className="space-y-2">
          {FIELD_ROLE_OPTIONS.map(o => (
            <label key={o.value} className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={draft.includes(o.value)} onCheckedChange={() => toggle(o.value)} data-testid={`file-share-role-${o.value}`} />
              <span>{o.label}</span>
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2 mt-3">
          <Button size="sm" variant="ghost" onClick={() => setOpen(false)} className="text-gray-400 hover:text-white">Cancel</Button>
          <Button size="sm" onClick={save} disabled={saving} className="bg-[#F5A623] text-black hover:bg-[#e09a1f]" data-testid="file-share-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
